import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, MapPin, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interests: [], // Changed to array for multiple selections
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const recaptchaRef = useRef(null);

  const interestOptions = [
    { value: 'donate', label: 'I want to donate' },
    { value: 'volunteer', label: 'I want to volunteer' },
    { value: 'partner', label: "I'm interested in partnering" },
    { value: 'school', label: 'I represent a school or organization' },
    { value: 'informed', label: 'Just keep me informed' },
    { value: 'other', label: 'Other' }
  ];

  const faqs = [
    {
      question: 'How do I donate to The Home Library Project?',
      answer: "You can donate directly through our website by clicking the 'Donate' button in the navigation. We accept one-time and recurring donations. Every $50 provides one child with 10 books for an entire year."
    },
    {
      question: 'When will The Home Library Project start serving students?',
      answer: "Distribution begins January 2027. Our first book deliveries will begin with Pre-K through 3rd grade students at Delaware schools with the highest need."
    },
    {
      question: 'Can I volunteer with The Home Library Project?',
      answer: "Yes! We're building our volunteer program and will have opportunities for book sorting, packing, and community events. Fill out the contact form above and select 'I want to volunteer' to stay informed."
    },
    {
      question: 'How do schools partner with The Home Library Project?',
      answer: "We're actively partnering with Delaware schools and early learning centers. If you represent a school or organization interested in bringing The Home Library Project to your students, please contact us using the form above or email info@thehomelibraryproject.org."
    },
    {
      question: 'Is The Home Library Project a registered nonprofit?',
      answer: "Yes, The Home Library Project is a 501(c)(3) nonprofit organization based in Delaware. All donations are tax-deductible to the extent allowed by law."
    },
    {
      question: 'How are books selected for each child?',
      answer: "Every book is carefully curated by educators and literacy experts. Each year's set includes 4 books aligned to Delaware's curriculum (financial literacy, Black history, STEM, SEL) and 6 high-interest books chosen for joy, diversity, and engagement, all matched to age and reading level."
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInterestToggle = (value) => {
    const currentInterests = formData.interests;
    let newInterests;
    
    if (currentInterests.includes(value)) {
      // Remove if already selected
      newInterests = currentInterests.filter(i => i !== value);
    } else {
      // Add if not selected (max 3)
      if (currentInterests.length < 3) {
        newInterests = [...currentInterests, value];
      } else {
        // Show error if trying to select more than 3
        toast.error('You can select up to 3 options');
        return;
      }
    }
    
    setFormData({ ...formData, interests: newInterests });
    setErrors({ ...errors, interests: '' });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (formData.interests.length === 0) {
      newErrors.interests = 'Please select at least one option';
    }
    if (!formData.message.trim()) newErrors.message = 'Please share your message';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      // Get reCAPTCHA token if available
      let recaptchaToken = null;
      if (recaptchaRef.current) {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset(); // Reset reCAPTCHA after getting token
      }

      // Convert interests array to comma-separated string for backend
      const submitData = {
        ...formData,
        interest: formData.interests.join(', '),
        recaptchaToken
      };
      const response = await axios.post(`${API}/contact`, submitData);
      if (response.data.success) {
        setShowSuccess(true);
        // Reset form after successful submission
        setFormData({ name: '', email: '', phone: '', interests: [], message: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or email us directly at info@thehomelibraryproject.org');
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-[#2c5570] to-[#14919f] text-white pt-[140px] pb-[80px] bg-cover bg-top" style={{ backgroundImage: `url('/bg-contact-hero.jpg')` }}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2c5570]/85 to-[#14919f]/85"></div>
        <div className="relative mx-auto max-w-[1240px] px-12">
          <h1 className="text-[40px] sm:text-[56px] font-extrabold leading-tight text-center mb-6" style={{letterSpacing: '0'}}>
            Have Questions? Let's Connect
          </h1>
          <p className="text-[19px] sm:text-[22px] text-white/90 leading-[1.6] text-center max-w-[800px] mx-auto mb-16">
            Whether you're a school, a potential partner, or simply curious about our work, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form + Info Section */}
      <section className="bg-white py-[100px]">
        <div className="mx-auto max-w-[1240px] px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-16">
            {/* Left Column: Contact Form */}
            <div>
              <div className="bg-white p-12 border border-[#e5e5e5] rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.06)]">
                {showSuccess ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-[#14919f]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-[#14919f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-3xl font-bold text-[#2c5570] mb-4">Thank You!</h3>
                    <p className="text-lg text-[#666] mb-2">
                      We've received your message and will be in touch shortly.
                    </p>
                    <p className="text-sm text-[#999]">
                      You can close this page or continue browsing our site.
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-[28px] font-bold text-[#2c5570] mb-4">Get in Touch</h2>
                    <p className="text-base text-[#666] mb-10">
                      Fill out the form below and we'll get back to you within 24 hours.
                    </p>

                    <form onSubmit={handleSubmit}>
                      {/* Name Field */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                          Your Name <span className="text-[#b63435]">*</span>
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="First and last name"
                          className="h-[50px] text-base px-[18px] border-[#ddd] rounded-lg focus:border-[#14919f] focus:ring-[#14919f]/10"
                          aria-required="true"
                        />
                        {errors.name && <p className="text-[13px] text-[#b63435] mt-1" role="alert">{errors.name}</p>}
                      </div>

                      {/* Email Field */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                          Email Address <span className="text-[#b63435]">*</span>
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          className="h-[50px] text-base px-[18px] border-[#ddd] rounded-lg focus:border-[#14919f] focus:ring-[#14919f]/10"
                          aria-required="true"
                        />
                        {errors.email && <p className="text-[13px] text-[#b63435] mt-1" role="alert">{errors.email}</p>}
                      </div>

                      {/* Phone Field */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                          Phone Number (Optional)
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(302) 555-0123"
                          className="h-[50px] text-base px-[18px] border-[#ddd] rounded-lg focus:border-[#14919f] focus:ring-[#14919f]/10"
                        />
                      </div>

                      {/* Interest Selection */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-3">
                          How would you like to help? <span className="text-[#b63435]">*</span>
                          <span className="block text-xs text-[#666] font-normal mt-1">(Select up to 3 options)</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {interestOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleInterestToggle(option.value)}
                              className={`p-4 border-2 rounded-lg text-left transition-all duration-200 ${
                                formData.interests.includes(option.value)
                                  ? 'border-[#14919f] bg-[#f0f8fa] text-[#2c5570]'
                                  : 'border-[#e5e5e5] text-[#666] hover:border-[#14919f]'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{option.label}</span>
                                {formData.interests.includes(option.value) && (
                                  <svg className="w-5 h-5 text-[#14919f]" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                        {errors.interests && <p className="text-[13px] text-[#b63435] mt-2" role="alert">{errors.interests}</p>}
                      </div>

                      {/* Message Field */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-[#2c5570] mb-2">
                          Tell us more <span className="text-[#b63435]">*</span>
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          placeholder="Share your thoughts, questions, or how you'd like to get involved..."
                          className="w-full p-[14px_18px] border border-[#ddd] rounded-lg text-base resize-vertical focus:border-[#14919f] focus:ring-2 focus:ring-[#14919f]/10 focus:outline-none"
                          aria-required="true"
                        />
                        {errors.message && <p className="text-[13px] text-[#b63435] mt-1" role="alert">{errors.message}</p>}
                      </div>

                      {/* reCAPTCHA */}
                      {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                        <div className="mb-6">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            size="invisible"
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                          />
                        </div>
                      )}

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-auto bg-[#fad53a] text-black text-lg font-bold py-4 px-12 rounded-[10px] hover:bg-[#f5c91a] hover:scale-[1.02] active:scale-[0.98] transition-all duration-250 shadow-none hover:shadow-[0_6px_20px_rgba(250,213,58,0.4)]"
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                      </Button>

                      <p className="text-[13px] text-[#999] italic text-center mt-4">
                        We respect your privacy. Your information will never be shared.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Right Column: Contact Info */}
            <div className="py-8">
              {/* Contact Information */}
              <div className="mb-12">
                <h3 className="text-[22px] font-bold text-[#2c5570] mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <a href="mailto:info@thehomelibraryproject.org" className="flex items-center gap-4 group">
                    <div className="flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#14919f]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#666] mb-1">Email</div>
                      <div className="text-base text-[#2c5570] group-hover:text-[#14919f] transition-colors">
                        info@thehomelibraryproject.org
                      </div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#14919f]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#666] mb-1">Location</div>
                      <div className="text-base text-[#666]">Delaware</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#14919f]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#666] mb-1">Response Time</div>
                      <div className="text-base text-[#666]">We respond within 24 hours</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-[#e5e5e5] my-12"></div>

              {/* Quick Links Card */}
              <div className="bg-gradient-to-br from-[#f0f8fa] to-white p-10 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <h3 className="text-xl font-bold text-[#2c5570] mb-6">Looking for something specific?</h3>
                
                <div className="space-y-4">
                  <a href="/about#mission" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Learn about our mission</span>
                  </a>
                  <a href="/#solution" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>See how we work</span>
                  </a>
                  <Link to="/donate" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Make a donation</span>
                  </Link>
                  <a href="/about#team" className="flex items-center gap-2 text-base text-[#14919f] hover:text-[#117a86] hover:pl-2 transition-all duration-200 group">
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    <span>Meet the founders</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#f8f9fa] py-[100px]">
        <div className="mx-auto max-w-[900px] px-8">
          <h2 className="text-[42px] font-bold text-[#2c5570] text-center mb-16">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] cursor-pointer"
                onClick={() => toggleFaq(index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-[#2c5570] pr-4">{faq.question}</h3>
                  <ChevronDown
                    className={`w-6 h-6 text-[#14919f] flex-shrink-0 transition-transform duration-300 ${
                      expandedFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedFaq === index ? 'max-h-[500px] mt-4' : 'max-h-0'
                  }`}
                >
                  <p className="text-base text-[#666] leading-[1.7]">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
